import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./csaApi";
import CsaFrameworkPublish from "./CsaFrameworkPublish";
import CsaScoringSettings from "./CsaScoringSettings";
import "./CsaAdminPanel.css";

/**
 * CSA administration, inside the existing Admin Settings modal (design §7c).
 *
 * The rest of that modal administers HSE and Electrical — sector types,
 * criticality options, score tables. CSA has none of those; it has no
 * criticality at all. So an Admin opening it found nothing about CSA, which is
 * what this section is for.
 *
 * §7c gives the Admin framework versions, scoring config and user roles. Two of
 * those are shown and not editable, for reasons stated on screen; the third —
 * granting roles — is the one that was blocking real work, because a reviewer
 * can only be nominated from people holding the Reviewer role and only an Admin
 * can grant it.
 */

const ROLE_HELP = {
  Auditor: "Create audits, answer, capture photos, submit.",
  Reviewer: "Sign off or return a submitted audit — never their own.",
  ProjectManager: "See every audit in the system. Schedule and reassign.",
  Admin: "Framework administration and granting roles. Does NOT grant access to other people's audits.",
};

export default function CsaAdminPanel() {
  const [fw, setFw] = useState(null);
  const [people, setPeople] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState(null);
  const [q, setQ] = useState("");

  /** Pending role edits, keyed by user id, until Save is pressed. */
  const [draft, setDraft] = useState({});

  const load = useCallback(() => {
    setError(null);
    Promise.all([api.adminFramework(), api.adminUsers()])
      .then(([f, u]) => {
        setFw(f);
        setPeople(u.people || []);
        setAllRoles(u.roles || []);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const rolesOf = useCallback(
    (p) => (draft[p.id] ? draft[p.id] : p.roles),
    [draft]
  );

  const toggle = (p, role) => {
    const current = rolesOf(p);
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    setDraft((d) => ({ ...d, [p.id]: next }));
  };

  const save = async (p) => {
    setBusyId(p.id);
    setError(null);
    try {
      const r = await api.adminSetRoles(p.id, rolesOf(p));
      setPeople((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, roles: r.roles, inferred: false } : x))
      );
      setDraft((d) => {
        const next = { ...d };
        delete next[p.id];
        return next;
      });
      setFlash(`${r.name}: ${r.roles.length ? r.roles.join(", ") : "no roles"}`);
      setTimeout(() => setFlash(null), 4000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const shown = useMemo(() => {
    if (!people) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return people;
    return people.filter(
      (p) =>
        String(p.name).toLowerCase().includes(needle) ||
        String(p.email).toLowerCase().includes(needle)
    );
  }, [people, q]);

  const counts = useMemo(() => {
    const m = {};
    (people || []).forEach((p) => p.roles.forEach((r) => { m[r] = (m[r] || 0) + 1; }));
    return m;
  }, [people]);

  if (error && !fw) return <div className="csa-admin"><p className="ca-error">{error}</p></div>;
  if (!fw || !people) return <div className="csa-admin"><p className="ca-muted">Loading CSA settings…</p></div>;

  const a = fw.active;

  return (
    <div className="csa-admin">
      <h3 className="ca-h">CSA</h3>

      {error ? <p className="ca-error">{error}</p> : null}
      {flash ? <p className="ca-flash">{flash}</p> : null}

      {/* ---------------- the framework ---------------- */}
      <section className="ca-card">
        <div className="ca-card-head">
          <h4>Framework</h4>
          <span className="ca-badge">{a ? a.version : "none"}</span>
        </div>

        {a ? (
          <>
            <p className="ca-shape">
              <strong>{a.pillars}</strong> pillars ·{" "}
              <strong>{a.sub_pillars}</strong> sub-pillars ·{" "}
              <strong>{a.elements}</strong> elements ·{" "}
              <strong>{a.questions}</strong> questions
            </p>

            {fw.usage && fw.usage.length ? (
              <p className="ca-muted ca-usage">
                Audits on this version:{" "}
                {fw.usage.map((u) => `${u.count} ${u.status}`).join(" · ")}
              </p>
            ) : null}

            <div className="ca-note">
              <strong>The questions themselves are read-only.</strong> Every audit
              is pinned to a version, so changing a question has to produce a new
              one — which is also what makes two sites on the same version
              comparable. Publish one below. The scoring settings are a separate
              matter and can be changed in place.
            </div>


          </>
        ) : (
          <p className="ca-muted">No framework version is active.</p>
        )}
      </section>

      {/* ---------------- §10d: editable in place ---------------- */}
      <CsaScoringSettings fw={fw} onSaved={load} />

      {/* ---------------- publishing ---------------- */}
      <CsaFrameworkPublish
        activeVersion={a ? a.version : null}
        onActivated={load}
      />

      {/* ---------------- roles ---------------- */}
      <section className="ca-card">
        <div className="ca-card-head">
          <h4>People and roles</h4>
          <span className="ca-counts">
            {allRoles.map((r) => (
              <span key={r}>
                {r} <strong>{counts[r] || 0}</strong>
              </span>
            ))}
          </span>
        </div>

        <p className="ca-muted">
          Only an Admin can grant these. A reviewer can only be nominated on a
          new audit from people holding <strong>Reviewer</strong>, so if that
          count is 1 there is one possible nominee — and nobody can review their
          own work.
        </p>

        <input
          className="ca-search"
          type="search"
          value={q}
          placeholder="Search name or email"
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="ca-scroll">
          <table className="ca-table people">
            <thead>
              <tr>
                <th>Person</th>
                {allRoles.map((r) => (
                  <th key={r} className="c" title={ROLE_HELP[r] || ""}>
                    {r === "ProjectManager" ? "Project Mgr" : r}
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => {
                const current = rolesOf(p);
                const dirty = !!draft[p.id];
                return (
                  <tr key={p.id} className={dirty ? "dirty" : ""}>
                    <td>
                      <span className="ca-name">{p.name}</span>
                      <span className="ca-email">{p.email}</span>
                      {p.inferred ? (
                        <span
                          className="ca-inferred"
                          title="No roles were ever set for this account; these are inferred from the old user_role column."
                        >
                          inferred
                        </span>
                      ) : null}
                    </td>
                    {allRoles.map((r) => (
                      <td key={r} className="c">
                        <input
                          type="checkbox"
                          checked={current.includes(r)}
                          onChange={() => toggle(p, r)}
                          aria-label={`${r} for ${p.name}`}
                        />
                      </td>
                    ))}
                    <td className="c">
                      {dirty ? (
                        <button
                          type="button"
                          className="ca-save"
                          disabled={busyId === p.id}
                          onClick={() => save(p)}
                        >
                          {busyId === p.id ? "Saving…" : "Save"}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!shown.length ? <p className="ca-muted">Nobody matches “{q}”.</p> : null}
      </section>
    </div>
  );
}
